<template>
  <div class="vanilla-c-wrap">
    <main class="screen" role="main">

      <div class="header">
        <button
          type="button"
          class="back-button"
          @click="goBack"
          :disabled="currentIndex === 0"
          aria-label="Zurück"
        >←</button>
        <span class="question-counter">
          Frage {{ currentIndex + 1 }} von {{ questions.length }}
        </span>
      </div>

      <div
        class="progress-bar"
        role="progressbar"
        :aria-valuenow="currentIndex + 1"
        aria-valuemin="1"
        :aria-valuemax="questions.length"
        aria-label="Fortschritt"
      >
        <div
          v-for="seg in progressSegments"
          :key="seg.sectionId"
          class="progress-segment"
          :style="{ flex: seg.size }"
        >
          <div
            v-if="seg.fill > 0"
            class="progress-segment-fill"
            :style="{ width: (seg.fill * 100) + '%' }"
          ></div>
        </div>
      </div>

      <div class="conversation" ref="conversation">
        <template v-for="item in conversationItems" :key="item.key">
          <div v-if="item.type === 'divider'" class="section-divider">{{ item.label }}</div>
          <div v-else-if="item.type === 'question'" class="bubble-question">{{ item.text }}</div>
          <div v-else-if="item.type === 'answer'" class="bubble-answer">{{ item.text }}</div>
        </template>
      </div>

      <div class="answer-area">
        <button
          v-if="currentQuestion.tooltip"
          type="button"
          class="tooltip-trigger"
          :aria-expanded="String(tooltipOpen)"
          :aria-controls="tooltipId"
          @click="tooltipOpen = !tooltipOpen"
        >
          <span class="tooltip-icon" aria-hidden="true">i</span>
          Was zählt hier?
        </button>

        <div
          v-if="currentQuestion.tooltip && tooltipOpen"
          :id="tooltipId"
          class="tooltip-content"
        >
          {{ currentQuestion.tooltip }}
        </div>

        <div
          class="answer-chips"
          role="radiogroup"
          :aria-label="currentQuestion.title"
          :key="currentQuestion.id"
        >
          <label
            v-for="opt in currentQuestion.options"
            :key="opt.value"
            class="chip"
          >
            <input
              type="radio"
              :name="`q-${currentQuestion.id}`"
              :value="opt.value"
              @change="selectAnswer(opt)"
            >
            <span class="chip-label">{{ opt.shortLabel || opt.label }}</span>
          </label>
        </div>
      </div>

    </main>
  </div>
</template>

<script>
import { sections, questions } from '../_data/sampleQuestions.js';

export default {
  name: 'VanillaC',
  data() {
    return {
      questions,
      currentIndex: 0,
      tooltipOpen: false,
      answers: {},
    };
  },
  computed: {
    sectionsWithCounts() {
      return sections
        .map(s => {
          const sectionQuestions = this.questions.filter(q => q.sectionId === s.id);
          let firstIdx = -1;
          let lastIdx = -1;
          this.questions.forEach((q, i) => {
            if (q.sectionId === s.id) {
              if (firstIdx === -1) firstIdx = i;
              lastIdx = i;
            }
          });
          return { ...s, count: sectionQuestions.length, firstIdx, lastIdx };
        })
        .filter(s => s.count > 0);
    },
    currentQuestion() {
      return this.questions[this.currentIndex];
    },
    progressSegments() {
      return this.sectionsWithCounts.map(s => {
        let fill = 0;
        if (this.currentIndex > s.lastIdx) {
          fill = 1;
        } else if (this.currentIndex >= s.firstIdx) {
          fill = (this.currentIndex - s.firstIdx + 1) / s.count;
        }
        return { sectionId: s.id, size: s.count, fill };
      });
    },
    conversationItems() {
      const items = [];
      let lastSectionId = null;
      for (let i = 0; i <= this.currentIndex; i++) {
        const q = this.questions[i];
        if (q.sectionId !== lastSectionId) {
          const section = this.sectionsWithCounts.find(s => s.id === q.sectionId);
          items.push({ key: `div-${q.sectionId}-${i}`, type: 'divider', label: section?.label || '' });
          lastSectionId = q.sectionId;
        }
        items.push({ key: `q-${q.id}`, type: 'question', text: q.title });
        const answerValue = this.answers[q.id];
        if (answerValue !== undefined) {
          const opt = q.options.find(o => o.value === answerValue);
          items.push({ key: `a-${q.id}`, type: 'answer', text: opt ? opt.label : '' });
        }
      }
      return items;
    },
    tooltipId() {
      return `tooltip-q${this.currentQuestion.id}`;
    },
  },
  mounted() {
    this.scrollConversationToBottom();
  },
  updated() {
    this.scrollConversationToBottom();
  },
  methods: {
    selectAnswer(opt) {
      this.answers = { ...this.answers, [this.currentQuestion.id]: opt.value };
      this.tooltipOpen = false;
      if (this.currentIndex < this.questions.length - 1) {
        this.$nextTick(() => {
          this.currentIndex++;
        });
      } else {
        this.$nextTick(() => {
          this.$router.push('/postsurvey/email');
        });
      }
    },
    goBack() {
      if (this.currentIndex === 0) return;
      const prev = this.questions[this.currentIndex - 1];
      const newAnswers = { ...this.answers };
      delete newAnswers[prev.id];
      this.answers = newAnswers;
      this.currentIndex--;
      this.tooltipOpen = false;
    },
    scrollConversationToBottom() {
      const el = this.$refs.conversation;
      if (el) el.scrollTop = el.scrollHeight;
    },
  },
};
</script>

<style scoped>
.vanilla-c-wrap {
  --color-bg-page: #f5f4ef;
  --color-bg-card: #ffffff;
  --color-bg-muted: #f1efe8;
  --color-text-primary: #2c2c2a;
  --color-text-secondary: #5f5e5a;
  --color-text-tertiary: #888780;
  --color-border: rgba(0, 0, 0, 0.15);
  --color-accent: #534AB7;
  --color-accent-light: #EEEDFE;
  --radius-md: 8px;
  --radius-lg: 12px;
  --radius-bubble: 18px;
  --font-sans: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;

  background: var(--color-bg-page);
  font-family: var(--font-sans);
  color: var(--color-text-primary);
  line-height: 1.5;
  min-height: 100%;
  padding: 1.25rem;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  box-sizing: border-box;
}

.vanilla-c-wrap *,
.vanilla-c-wrap *::before,
.vanilla-c-wrap *::after {
  box-sizing: border-box;
}

.screen {
  width: 100%;
  max-width: 420px;
  background: var(--color-bg-card);
  border-radius: var(--radius-lg);
  border: 0.5px solid var(--color-border);
  padding: 1rem 1.25rem 0;
  min-height: 600px;
  display: flex;
  flex-direction: column;
}

.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
}

.back-button {
  background: transparent;
  border: none;
  padding: 4px 8px;
  cursor: pointer;
  font-size: 20px;
  color: var(--color-text-secondary);
  line-height: 1;
}

.back-button:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.question-counter {
  font-size: 13px;
  color: var(--color-text-secondary);
}

.progress-bar {
  display: flex;
  gap: 4px;
  margin-bottom: 1.25rem;
}

.progress-segment {
  height: 6px;
  border-radius: 3px;
  background: var(--color-bg-muted);
  overflow: hidden;
}

.progress-segment-fill {
  height: 100%;
  background: var(--color-accent);
  border-radius: 3px;
  transition: width 0.25s ease;
}

.conversation {
  flex: 1;
  overflow-y: auto;
  margin: 0 -1.25rem;
  padding: 0 1.25rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.section-divider {
  text-align: center;
  font-size: 12px;
  color: var(--color-text-tertiary);
  padding: 8px 0;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.bubble-question {
  align-self: flex-start;
  max-width: 85%;
  background: var(--color-bg-muted);
  border-radius: var(--radius-bubble);
  border-bottom-left-radius: 6px;
  padding: 12px 16px;
  font-size: 15px;
  color: var(--color-text-primary);
  line-height: 1.4;
}

.bubble-answer {
  align-self: flex-end;
  max-width: 85%;
  background: var(--color-accent);
  color: white;
  border-radius: var(--radius-bubble);
  border-bottom-right-radius: 6px;
  padding: 10px 16px;
  font-size: 14px;
  line-height: 1.4;
}

.answer-area {
  background: var(--color-bg-card);
  border-top: 0.5px solid var(--color-border);
  margin: 0 -1.25rem;
  padding: 1rem 1.25rem 1.25rem;
  position: sticky;
  bottom: 0;
}

.tooltip-trigger {
  background: transparent;
  border: none;
  padding: 4px 0;
  font-size: 13px;
  color: var(--color-accent);
  cursor: pointer;
  margin-bottom: 8px;
  font-family: inherit;
  display: flex;
  align-items: center;
  gap: 6px;
}

.tooltip-trigger:hover {
  text-decoration: underline;
}

.tooltip-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: var(--color-accent);
  color: white;
  font-size: 10px;
  font-style: italic;
  font-family: Georgia, serif;
  line-height: 1;
}

.tooltip-content {
  background: var(--color-bg-muted);
  border-radius: var(--radius-md);
  padding: 10px 12px;
  font-size: 13px;
  color: var(--color-text-secondary);
  margin-bottom: 10px;
  line-height: 1.5;
}

.answer-chips {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}

.chip {
  position: relative;
}

.chip input[type="radio"] {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}

.chip-label {
  display: block;
  text-align: center;
  padding: 12px 8px;
  border: 0.5px solid var(--color-border);
  border-radius: var(--radius-md);
  font-size: 13px;
  color: var(--color-text-primary);
  cursor: pointer;
  background: var(--color-bg-card);
  transition: background 0.15s, border-color 0.15s, color 0.15s;
  line-height: 1.3;
}

.chip-label:hover {
  background: var(--color-bg-muted);
}

.chip input:checked + .chip-label {
  border-color: var(--color-accent);
  background: var(--color-accent);
  color: white;
}
</style>
